import { forwardRef, useImperativeHandle, useState } from "react";
import styled from "styled-components";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Button as LatitudeButton } from "@flexport/latitude-typescript";
import { trackBulkDownload } from "../../util/downloadUtils";
import { QuarantineStatus } from "../../generated/graphql";
import ConfirmDialog from "../../components/ConfirmDialog";
import { isSessionExpiredError } from "../../contexts/SessionExpiredContext";
import AdminFileGrid from "./grids/AdminFileGrid";

const ADMIN_FILES_QUERY = gql`
  query GetAdminFiles {
    admin {
      files {
        id
        fileName
        extension
        createdAt
        fileUrl
        folderPath
        subfolderPath
        clientId
        reviewedByCurrentUser
        quarantineStatus
        quarantineReason
        quarantineTimestamp
        scanEngine
        scanEngineVersion
        scanStartedAt
        scanCompletedAt
        client {
          clientId
          client_name
          folder
        }
        user {
          id
          name
          role
        }
      }
    }
  }
`;

const ADMIN_CLIENTS_QUERY = gql`
  query GetAdminClients {
    admin {
      clients {
        clientId
        client_name
        folder
      }
    }
  }
`;

const ADMIN_DELETE_FILE_MUTATION = gql`
  mutation AdminDeleteFile($id: ID!) {
    admin {
      deleteFile(id: $id)
    }
  }
`;

const GET_CLIENT_FILES_ZIP_URL = gql`
  mutation GetClientFilesZipUrl($clientId: ID!) {
    admin {
      getClientFilesZipUrl(clientId: $clientId) {
        status
        downloadUrl
        fileCount
        estimatedSize
        error
      }
    }
  }
`;

const FileListContainer = styled.div`
  margin: 20px 0;
`;

const FileListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const FileListTitle = styled.h2`
  font-size: 20px;
  margin: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const FilterLabel = styled.label`
  font-weight: 500;
  margin-right: 5px;
`;

const FilterSelect = styled.select`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 14px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const Loading = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #dc3545;
`;

interface FileData {
  id: string;
  fileName: string;
  extension: string;
  createdAt: string;
  fileUrl: string | null;
  folderPath?: string;
  subfolderPath?: string;
  clientId: string;
  quarantineStatus: QuarantineStatus;
  quarantineReason?: string | null;
  quarantineTimestamp?: string | null;
  scanEngine?: string | null;
  scanEngineVersion?: string | null;
  scanStartedAt?: string | null;
  scanCompletedAt?: string | null;
  client: {
    clientId: string;
    client_name: string;
    folder: string;
  };
  user?: {
    id: string;
    name: string;
    role: string;
  };
}

interface ClientData {
  clientId: string;
  client_name: string;
  folder: string;
}

// Define the ref type explicitly
export interface FileListRefType {
  refetch: () => void;
}

interface AdminFileListProps {}

const AdminFileList = forwardRef<FileListRefType, AdminFileListProps>(
  (_, ref) => {
    const [selectedClientId, setSelectedClientId] = useState<string | "all">(
      "all",
    );

    // State for custom delete confirmation dialog
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<{
      id: string;
      name: string;
    } | null>(null);

    const {
      loading: filesLoading,
      error: filesError,
      data: filesData,
      refetch: refetchFiles,
    } = useQuery(ADMIN_FILES_QUERY, {
      fetchPolicy: "network-only", // Don't use cache for this query
    });

    const {
      loading: clientsLoading,
      error: clientsError,
      data: clientsData,
    } = useQuery(ADMIN_CLIENTS_QUERY, {
      fetchPolicy: "network-only",
    });

    const [deleteFile] = useMutation(ADMIN_DELETE_FILE_MUTATION);
    const [getZipUrl, { loading: downloadLoading }] = useMutation(
      GET_CLIENT_FILES_ZIP_URL,
    );

    // Expose refetch method to parent components
    useImperativeHandle(ref, () => ({
      refetch: refetchFiles,
    }));

    const handleDeleteFile = (id: string) => {
      // Find the file to get its name for the confirmation message
      const allFiles = filesData?.admin?.files || [];
      const file = allFiles.find((f: FileData) => f.id === id);
      const fileName = file?.fileName || "this file";

      // Show custom confirmation dialog
      setFileToDelete({ id, name: fileName });
      setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
      if (!fileToDelete) return;

      const { id } = fileToDelete;
      setShowDeleteDialog(false);

      try {
        await deleteFile({
          variables: { id },
          // Force Apollo to bypass cache and update data from server
          update: (cache) => {
            // Invalidate and remove this file from the cache
            cache.evict({ id: `FileData:${id}` });
            cache.gc();
          },
          // Force refetch after deletion to get updated data
          refetchQueries: [
            {
              query: ADMIN_FILES_QUERY,
              fetchPolicy: "network-only",
            },
          ],
        });

        // Explicitly refetch from server to ensure UI updates
        await refetchFiles({ fetchPolicy: "network-only" });
        setFileToDelete(null);
      } catch (error) {
        console.error("Error deleting file:", error);
        // Skip alert when session-expired modal will handle the error
        if (!isSessionExpiredError()) {
          alert("Failed to delete the file. Please try again.");
        }
        setFileToDelete(null);
      }
    };

    const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedClientId(e.target.value);
    };

    const handleDownloadAll = async () => {
      if (selectedClientId === "all") {
        alert("Please select a specific client to download files");
        return;
      }

      try {
        const response = await getZipUrl({
          variables: { clientId: selectedClientId },
        });

        const result = response.data?.admin?.getClientFilesZipUrl;

        if (result?.status === "ready" && result?.downloadUrl) {
          // Track the download
          await trackBulkDownload({
            clientId: selectedClientId,
            fileCount: result.fileCount || 0,
            zipUrl: result.downloadUrl,
          });

          // Handle relative URLs - convert to absolute URL if needed
          let downloadUrl = result.downloadUrl;

          // Check if we're in local development and if the URL is relative
          if (downloadUrl.startsWith("/")) {
            // In development, the backend runs on a configurable port
            if (window.location.hostname === "localhost") {
              const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";
              downloadUrl = `http://localhost:${backendPort}${downloadUrl}`;
            } else {
              // In production, use the same hostname but with the relative path
              downloadUrl = `${window.location.origin}${downloadUrl}`;
            }
          }

          // Open in a new tab to avoid navigation issues
          window.open(downloadUrl, "_blank");
        } else if (result?.error) {
          alert(`Error preparing download: ${result.error}`);
        } else {
          alert(
            "Unable to prepare files for download. Please try again later.",
          );
        }
      } catch (error) {
        console.error("Error getting download URL:", error);
        if (!isSessionExpiredError()) {
          alert("Failed to generate download link");
        }
      }
    };

    if (filesLoading || clientsLoading)
      return <Loading>Loading data...</Loading>;
    if (filesError)
      return (
        <ErrorMessage>Error loading files: {filesError.message}</ErrorMessage>
      );
    if (clientsError)
      return (
        <ErrorMessage>
          Error loading clients: {clientsError.message}
        </ErrorMessage>
      );

    const allFiles = filesData?.admin?.files || [];
    const clients = clientsData?.admin?.clients || [];

    // Filter files based on selected client
    const filteredFiles =
      selectedClientId === "all"
        ? allFiles
        : allFiles.filter(
            (file: FileData) => file.clientId === selectedClientId,
          );

    return (
      <FileListContainer>
        <FileListHeader>
          <FileListTitle>Managed Files</FileListTitle>
          <FilterContainer>
            <FilterLabel htmlFor="client-filter">Client:</FilterLabel>
            <FilterSelect
              id="client-filter"
              value={selectedClientId}
              onChange={handleClientChange}
            >
              <option value="all">All Clients</option>
              {clients.map((client: ClientData) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.client_name}
                </option>
              ))}
            </FilterSelect>

            <LatitudeButton
              label="Download All Files"
              intent="basic"
              kind="hollow"
              size="m"
              isLoading={downloadLoading}
              width="responsive"
              type="button"
              disabled={
                downloadLoading ||
                selectedClientId === "all" ||
                filteredFiles.length === 0
              }
              onClick={handleDownloadAll}
            >
              {downloadLoading ? "Preparing..." : "Download All Files"}
            </LatitudeButton>
          </FilterContainer>
        </FileListHeader>

        {filteredFiles.length === 0 ? (
          <EmptyMessage>No files found for the selected criteria.</EmptyMessage>
        ) : (
          <AdminFileGrid rows={filteredFiles} onDelete={handleDeleteFile} />
        )}

        {/* Custom delete confirmation dialog */}
        <ConfirmDialog
          isOpen={showDeleteDialog}
          title="File Uploader"
          message={`Are you sure you want to permanently delete "${fileToDelete?.name}"?\n\nThis action cannot be undone.`}
          confirmText="OK"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setFileToDelete(null);
          }}
        />
      </FileListContainer>
    );
  },
);

// Add display name
AdminFileList.displayName = "AdminFileList";

export default AdminFileList;
